import React, { useState, useEffect, useMemo } from "react";
import { Link } from 'react-router-dom';
import { LoadingSpinner, ShiftOperations, CheckboxGroup } from "../components";
import { LIVE_OPERATIONS_TABS, LoadingSpinnerMeta, API_BASE_URL } from "../data";
import { LiveOperationsMetaProps, LiveStatusTab, Event } from "../data/types";
import { ChevronLeftIcon, ChevronDownIcon, FilterIcon } from "../icons";
import { CheckinShiftOperations } from "../components/live_operations/CheckinShiftOperations";
import { sortByName } from "../utils/sorting";

type Props = {
    meta: LiveOperationsMetaProps
}

export const LiveOperations = (props: Props) => {
    const { meta } = props
    const [activeTab, setActiveTab] = useState<LiveStatusTab>('checkin-stats');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [events, setEvents] = useState<Event[]>([]);
    const [selectedEventIds, setSelectedEventIds] = useState<number[]>([]);
    const [selectedAreaIds, setSelectedAreaIds] = useState<number[]>([]);

    // State for collapsing filters
    const [isEventFilterOpen, setIsEventFilterOpen] = useState(true);
    const [isAreaFilterOpen, setIsAreaFilterOpen] = useState(true);

    // Fetch Events on Mount and select all by default
    useEffect(() => {
        const fetchInitialEvents = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${API_BASE_URL}/event`, { credentials: "include" });
                if (!response.ok) throw new Error("Failed to fetch event details.");
                const rawData: Event[] = await response.json();
                setEvents(rawData);
                // Select all events by default
                setSelectedEventIds(rawData.map(e => e.id));
                setSelectedAreaIds(rawData.length === 1 ? rawData[0].areas?.map(area => area.id) || [] : []);
            }
            catch (err) {
                setError(err instanceof Error ? err.message : "Failed to fetch event information");
            }
            finally {
                setLoading(false);
            }
        }
        fetchInitialEvents();
    }, [])

    // Combine areas from all selected events
    const availableAreas = useMemo(() => {
        const selectedEvents = events.filter(e => selectedEventIds.includes(e.id));
        const areasMap = new Map<number, { id: number; name: string; eventName: string }>();

        selectedEvents.forEach(event => {
            event.areas?.forEach(area => {
                if (!areasMap.has(area.id)) {
                    areasMap.set(area.id, {
                        id: area.id,
                        name: selectedEvents.length > 1 ? `${area.name} (${event.name})` : area.name,
                        eventName: event.name
                    });
                }
            });
        });

        return Array.from(areasMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [events, selectedEventIds])

    const handleEventSelectClick = (value: string | number) => {
        const eventId = Number(value);

        if (selectedEventIds.includes(eventId)) {
            setSelectedEventIds(prev => prev.filter(id => id !== eventId));
            // Remove areas that belong to the deselected event
            const eventAreas = events.find(e => e.id === eventId)?.areas?.map(a => a.id) || [];
            setSelectedAreaIds(prev => prev.filter(id => !eventAreas.includes(id)));
        } else {
            setSelectedEventIds(prev => [...prev, eventId]);
        }
    }

    const handleSelectAllEvents = () => {
        if (selectedEventIds.length === events.length) {
            // Deselect all
            setSelectedEventIds([]);
            setSelectedAreaIds([]);
        } else {
            // Select all
            setSelectedEventIds(events.map(e => e.id));
        }
    }

    const selectContentPage = (tab: string) => {
        switch (tab) {
            case "shifts":
                return <ShiftOperations
                    meta={meta.shiftsMeta}
                    eventId={selectedEventIds[0]}
                    isActive={activeTab === "shifts"}
                    areaIds={selectedAreaIds}
                />
            case "checkin-stats":
                return <CheckinShiftOperations
                    isActive={activeTab === 'checkin-stats'}
                    areaIds={selectedAreaIds}
                />
            default:
                return <ShiftOperations meta={meta.shiftsMeta} eventId={selectedEventIds[0]} isActive={true} areaIds={selectedAreaIds} />
        }
    }

    const handleAreaSelectClick = (value: string | number) => {
        const areaId = Number(value);

        selectedAreaIds.includes(areaId) ? setSelectedAreaIds(prev => prev.filter(id => id !== areaId))
            : setSelectedAreaIds(prev => [...prev, areaId]);
    }

    // Toggle Select All Areas
    const handleSelectAllAreas = () => {
        if (availableAreas.length === 0) return;

        if (selectedAreaIds.length === availableAreas.length) {
            // Deselect All
            setSelectedAreaIds([]);
        } else {
            // Select All
            setSelectedAreaIds(availableAreas.map(a => a.id));
        }
    }

    return (
        <div className="flex-1 pb-8 pt-8">
            <div className="container mx-auto px-4">
                {/* Back to Admin Hub */}
                <div className='mb-4'>
                    <Link
                        to="/admin"
                        className="inline-flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                    >
                        <ChevronLeftIcon className="w-5 h-5 me-2" />
                        Back to Admin Hub
                    </Link>
                </div>

                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                        {meta.title}
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 text-lg mb-6">
                        {meta.description}
                    </p>
                </div>

                {loading && (
                    <div className="flex justify-center items-center min-h-[400px] mt-4">
                        <LoadingSpinner meta={LoadingSpinnerMeta} />
                    </div>
                )}

                {/* Error Section */}
                {error && (
                    <div className="max-w-2xl mx-auto">
                        <div className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-6 py-4 rounded-lg">
                            <p className="font-bold text-lg mb-2">{meta.errorTitle}</p>
                            <p>{error}</p>
                        </div>
                    </div>
                )}

                {!loading && !error && (
                    <>
                        {/* Collapsible Event Multi-Select */}
                        <div className="mb-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800 shadow-sm">
                            <button
                                onClick={() => setIsEventFilterOpen(!isEventFilterOpen)}
                                className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors z-10 relative"
                            >
                                <div className="flex items-center gap-2">
                                    <FilterIcon className="w-5 h-5 text-gray-500" />
                                    <span className="font-medium text-gray-700 dark:text-gray-200">
                                        Filter by Event
                                    </span>
                                    {selectedEventIds.length > 0 && (
                                        <span className="ms-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                                            {selectedEventIds.length} selected
                                        </span>
                                    )}
                                </div>
                                <div className={`transition-transform duration-300 ${isEventFilterOpen ? 'rotate-180' : 'rotate-0'}`}>
                                    <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                                </div>
                            </button>

                            <div
                                className={`transition-[max-height,opacity] duration-500 ease-in-out overflow-hidden ${isEventFilterOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                                    }`}
                            >
                                <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                                    <div className="flex justify-end gap-3 mb-3">
                                        <button
                                            onClick={handleSelectAllEvents}
                                            className="text-xs font-medium text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
                                        >
                                            {events.length === selectedEventIds.length ? 'Deselect All' : 'Select All'}
                                        </button>
                                    </div>

                                    <CheckboxGroup
                                        label="Select Events to Monitor"
                                        options={sortByName(events).map(e => ({ id: e.id, label: e.name }))}
                                        selectedValues={selectedEventIds}
                                        onChange={handleEventSelectClick}
                                        columns={2}
                                        bordered={false}
                                        className="w-full"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Collapsible Area Multi-Select */}
                        {selectedEventIds.length > 0 && availableAreas.length > 0 && (
                            <div className="mb-8 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800 shadow-sm">
                                <button
                                    onClick={() => setIsAreaFilterOpen(!isAreaFilterOpen)}
                                    className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors z-10 relative"
                                >
                                    <div className="flex items-center gap-2">
                                        <FilterIcon className="w-5 h-5 text-gray-500" />
                                        <span className="font-medium text-gray-700 dark:text-gray-200">
                                            {meta.areaCheckBoxTitle || "Filter by Area"}
                                        </span>
                                        {selectedAreaIds.length > 0 && (
                                            <span className="ms-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                                                {selectedAreaIds.length} selected
                                            </span>
                                        )}
                                    </div>
                                    <div className={`transition-transform duration-300 ${isAreaFilterOpen ? 'rotate-180' : 'rotate-0'}`}>
                                        <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                                    </div>
                                </button>

                                <div
                                    className={`transition-[max-height,opacity] duration-500 ease-in-out overflow-hidden ${isAreaFilterOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                                        }`}
                                >
                                    <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                                        <div className="flex justify-end gap-3 mb-3">
                                            <button
                                                onClick={handleSelectAllAreas}
                                                className="text-xs font-medium text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
                                            >
                                                {availableAreas.length === selectedAreaIds.length ? 'Deselect All' : 'Select All'}
                                            </button>
                                        </div>

                                        <CheckboxGroup
                                            label={meta.areaCheckBoxLabel}
                                            options={availableAreas.map(a => ({ id: a.id, label: a.name }))}
                                            selectedValues={selectedAreaIds}
                                            onChange={handleAreaSelectClick}
                                            columns={3}
                                            bordered={false}
                                            className="w-full"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Content Logic */}
                        {selectedEventIds.length === 0 ? (
                            <div className="flex flex-col items-center justify-center p-12 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                                <div className="text-gray-400 mb-4">
                                    <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-medium text-gray-900 dark:text-white">No Event Selected</h3>
                                <p className="text-gray-500 dark:text-gray-400 mt-2 text-center max-w-md">
                                    Please select at least one event from the filter above to view live statistics, shift operations, and volunteer status.
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* Tabs */}
                                <div className='flex justify-between border-b border-gray-200 dark:border-gray-700 mb-6'>
                                    <div className="flex -mb-px">
                                        {LIVE_OPERATIONS_TABS.map((tab) => (
                                            <button
                                                key={tab.key}
                                                type="button"
                                                onClick={() => setActiveTab(tab.key)}
                                                className={`px-6 py-3 text-sm font-medium border-b-2 rounded-t-lg transition-colors duration-200
                                                    ${activeTab === tab.key
                                                        ? "bg-primary-600 border-primary-600 text-gray-100 dark:text-primary-400 rounded-md"
                                                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                                                    }`}
                                            >
                                                {tab.header}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Main Content */}
                                <main className="space-y-6 pt-0 mt-0 min-w-0 overflow-x-auto">
                                    {selectContentPage(activeTab)}
                                </main>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
